import React, { useState } from "react";
import {
  Container,
  Grid2 as Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import { Zap, Star, Clock, Infinity, Check } from "lucide-react";
import config from "../config.json";

function PricingCard({
  title,
  subtitle,
  useGradient = false,
  price,
  period,
  type,
  lifetime,
  features,
  highlighted = false,
  ctaText = "Get Started",
}) {
  return (
    <Card
      sx={{
        p: { xs: 4, md: 2, lg: "1rem" },
        margin: { xs: "0 1rem", lg: "0" },
        borderRadius: "1rem",
        backgroundColor: highlighted ? "#2149D9" : "white",
        color: highlighted ? "white" : "inherit",
        boxShadow: 1,
        border: highlighted ? "2px solid #2149D9" : "2px solid #f0f0f0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "transform 0.3s, box-shadow 0.3s",
        transform: { md: highlighted ? "scale(1.05)" : "none" },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          {title === "Agency" && (
            <Star color={highlighted ? "white" : "#2149D9"} size={20} />
          )}
          {title === "Pro" && (
            <Zap color={highlighted ? "white" : "#2149D9"} size={20} />
          )}
          {title === "" && (
            <Clock color={highlighted ? "white" : "#2149D9"} size={20} />
          )}
          {title.startsWith("Lifetime") && (
            <Infinity color={highlighted ? "white" : "#2149D9"} size={20} />
          )}
          <Typography
            variant="h5"
            component="span"
            sx={{
              fontWeight: "bold",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: useGradient && "transparent",
              backgroundImage:
                useGradient &&
                "linear-gradient(to right,rgba(29,78,216,1),rgba(219,39,119,1))",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body1"
              component="span"
              sx={{ color: highlighted ? "white" : "gray" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          mb={4}
          display="flex"
          justifyContent={"flex-start"}
          alignItems="baseline"
        >
          <Typography variant="h4" component="span" sx={{ fontWeight: "bold" }}>
            ${price}
          </Typography>
          {period && (
            <Typography
              variant="body2"
              component="span"
              sx={{ color: highlighted ? "white" : "gray" }}
            >
              {period}
            </Typography>
          )}
        </Box>
        <List>
          {features.map((feature, index) => (
            <ListItem
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 2 }}
            >
              <Check
                size={20}
                style={{ minWidth: "20px" }}
                color={highlighted ? "white" : "#2149D9"}
              />
              <Typography
                variant="body2"
                sx={{
                  color:
                    type === "annual" &&
                    title === "Agency" &&
                    index === 0 &&
                    "#1d4ed8",
                }}
              >
                {feature}
              </Typography>
            </ListItem>
          ))}
        </List>
      </CardContent>
      <Button
        variant="contained"
        fullWidth
        sx={{
          py: 2,
          px: 4,
          borderRadius: "0.5rem",
          fontWeight: 600,
          backgroundColor: highlighted ? "white" : "#2149D9",
          color: highlighted ? "#2149D9" : "white",
          transition: "background-color 0.3s, color 0.3s",
          ":hover": {
            backgroundColor: highlighted ? "rgba(255,255,255,0.8)" : "#2149D9",
          },
          textTransform: "none",
        }}
        onClick={() => {
          let loginLink = "";
          if (lifetime) {
            loginLink = config.mainAppLink + "login/lifetime";
          } else {
            loginLink = config.mainAppLink + "login/subscribe";
          }
          window.open(loginLink, "_self");
        }}
      >
        {ctaText}
      </Button>
    </Card>
  );
}

const plans = [
  {
    title: "Starter",
    price: "29",
    period: "($7.25/audit)",
    type: "one-time",
    lifetime: false,
    features: [
      "4 Single Page Audits",
      "Category & Section Analysis",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Email Support",
    ],
  },
  {
    title: "Pro",
    subtitle: "(Recommended)",
    price: "49",
    period: "/month ($3.27/audit)",
    type: "monthly",
    lifetime: false,
    highlighted: true,
    features: [
      "15 Audits/mo",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Email Support",
      "$3.5 per extra credit",
    ],
  },
  {
    title: "Pro",
    subtitle: "(Recommended)",
    price: "39",
    period: "/month ($1.95/audit)",
    type: "annual",
    lifetime: false,
    highlighted: true,
    features: [
      "20 Audits/mo",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Email Support",
      "$3.5 per extra credit",
    ],
  },
  {
    title: "Agency",
    price: "99",
    period: "/month ($2.2/audit)",
    type: "monthly",
    lifetime: false,
    features: [
      "45 Audits/mo",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Priority Launch of New Features",
      "Priority Support",
      "$2.5 per extra credit",
    ],
  },
  {
    title: "Agency",
    price: "79",
    period: "/month ($1.32/audit)",
    type: "annual",
    lifetime: false,
    features: [
      "Host White-label audits on your subdomain with customizations",
      "60 Audits/mo",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Priority Launch of New Features",
      "Priority Support",
      "$2.5 per extra credit",
    ],
  },
  {
    title: "Lifetime Pro",
    useGradient: true,
    price: "490",
    period: null,
    type: "one-time",
    lifetime: true,
    features: [
      "15 Audits/mo for Lifetime",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Priority Support",
      "$3.5 per extra credit",
    ],
    ctaText: "Get Lifetime Access",
  },
  {
    title: "Lifetime Agency",
    useGradient: true,
    price: "990",
    period: null,
    type: "one-time",
    lifetime: true,
    features: [
      "45 Audits/mo for Lifetime",
      "Category & Section Analysis",
      "Export PDFs with 100% White labeling",
      "Host the White Labeling version on your domain",
      "Multi Device Analysis",
      "Personalized Recommendations",
      "Priority Support",
      "$2.5 per extra credit",
    ],
    ctaText: "Get Lifetime Access",
  },
];

const PricingPage = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  const handleSwitchChange = (event) => {
    setIsAnnual(event.target.checked);
  };

  const filteredPlans = plans.filter(
    (plan) =>
      plan.type === "one-time" ||
      (isAnnual ? plan.type === "annual" : plan.type === "monthly")
  );

  return (
    <Container
      align="center"
      justifyContent="center"
      disableGutters
      maxWidth={false}
    >
      <Typography
        variant="h4"
        fontWeight={900}
        sx={{ color: "#1d4ed8", paddingTop: { xs: "1rem", md: "2rem" } }}
        gutterBottom
      >
        Simple, Transparent Pricing
      </Typography>
      <Typography
        variant="body1"
        sx={{ marginBottom: "3rem", color: "rgb(75, 85, 99)" }}
      >
        For customized versions of of tools that fit your workflow, reach out to{" "}
        <strong style={{ color: "#1d4ed8" }}>support@getwebsite.report</strong>{" "}
        for the consultation.
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        marginBottom="2rem"
      >
        <Typography
          variant="body1"
          sx={{
            marginRight: "1rem",
            color: isAnnual ? "gray" : "#1d4ed8",
            fontWeight: isAnnual ? 400 : 600,
          }}
        >
          Monthly
        </Typography>
        <Switch
          checked={isAnnual}
          onChange={handleSwitchChange}
          color="primary"
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body1"
            sx={{
              color: isAnnual ? "#1d4ed8" : "gray",
              fontWeight: isAnnual ? 600 : 400,
            }}
          >
            Annual
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.75rem",
              color: isAnnual ? "#22c55e" : "gray",
              fontWeight: isAnnual ? 600 : 400,
            }}
          >
            (2 months free + 40% more value)
          </Typography>
        </Box>
      </Box>
      <Grid
        container
        spacing={4}
        justifyContent="center"
        marginBottom={4}
        sx={{ zoom: { xs: 1, lg: 0.8 } }}
      >
        {filteredPlans.map((plan, index) => (
          <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
            <PricingCard {...plan} />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default PricingPage;
